import React from 'react';
import { observer } from 'mobx-react';
import { observable, action } from 'mobx';
import moment from 'moment';
import styled from 'styled-components';
import { Dropdown } from 'semantic-ui-react';
import { Milestone } from '../../store/planning/Milestone';
import { BudgetStore, budgetTypeLabel } from '../../store/Budget';
import { api } from '../../store/Base';
import PhabricatorProjectPicker from './PhabricatorProjectPicker';

const C = {
    bg: '#ffffff', border: '#e0e3e8',
    textPrimary: '#1a1d23', textSecondary: '#5c6470', textMuted: '#9099a8',
    accent: '#2185d0', success: '#21ba45', danger: '#db2828',
    sidebarBg: '#f8f9fa', inputBg: '#f8f9fa',
};

const TYPE_OPTIONS = [
    { text: 'Sprint', value: 'sprint', color: '#3182ce' },
    { text: 'Feature', value: 'feature', color: '#805ad5' },
    { text: 'Milestone', value: 'milestone', color: '#dd6b20' },
    { text: 'Phase', value: 'phase', color: '#319795' },
];

const STATUS_OPTIONS = [
    { text: 'Planning', value: 'planning', color: '#805ad5' },
    { text: 'Active', value: 'active', color: '#3182ce' },
    { text: 'Completed', value: 'completed', color: '#38a169' },
    { text: 'On Hold', value: 'on_hold', color: '#dd6b20' },
];

const Panel = styled.div<{ visible: boolean }>`
    position: absolute; top: 0; right: 0; bottom: 0; width: 360px;
    background: ${C.bg}; box-shadow: -4px 0 20px rgba(0,0,0,0.12);
    z-index: 50; display: flex; flex-direction: column;
    transition: transform 0.25s ease;
    transform: ${props => props.visible ? 'translateX(0)' : 'translateX(100%)'};
    border-left: 1px solid ${C.border};
`;
const PanelHeader = styled.div`
    display: flex; justify-content: space-between; align-items: center;
    padding: 12px 16px; border-bottom: 1px solid ${C.border}; background: ${C.sidebarBg};
    flex-shrink: 0;
`;
const PanelTitle = styled.h3` font-size: 14px; font-weight: 600; color: ${C.textPrimary}; margin: 0; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; `;
const CloseBtn = styled.button` background: none; border: none; font-size: 20px; color: ${C.textMuted}; cursor: pointer; padding: 4px 8px; &:hover { color: ${C.textPrimary}; } `;
const PanelContent = styled.div` flex: 1; overflow-y: auto; padding: 16px; `;
const Section = styled.div` margin-bottom: 20px; `;
const SectionLabel = styled.label` display: block; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: ${C.textSecondary}; margin-bottom: 6px; `;
const TextInput = styled.input` width: 100%; padding: 8px 10px; background: ${C.inputBg}; border: 1px solid ${C.border}; border-radius: 4px; color: ${C.textPrimary}; font-size: 13px; font-family: inherit; &:focus { outline: none; border-color: ${C.accent}; background: #fff; } `;
const TextArea = styled.textarea` width: 100%; padding: 8px 10px; background: ${C.inputBg}; border: 1px solid ${C.border}; border-radius: 4px; color: ${C.textPrimary}; font-size: 13px; font-family: inherit; resize: vertical; min-height: 60px; &:focus { outline: none; border-color: ${C.accent}; background: #fff; } `;
const DateInput = styled.input` width: 100%; padding: 8px 10px; background: ${C.inputBg}; border: 1px solid ${C.border}; border-radius: 4px; color: ${C.textPrimary}; font-size: 13px; font-family: inherit; &:focus { outline: none; border-color: ${C.accent}; background: #fff; } `;
const BtnGroup = styled.div` display: flex; gap: 4px; flex-wrap: wrap; `;
const BtnChoice = styled.button<{ active: boolean; color: string }>` padding: 5px 12px; border-radius: 4px; font-size: 12px; font-weight: 500; cursor: pointer; border: 1px solid ${props => props.active ? props.color : C.border}; background: ${props => props.active ? props.color : C.bg}; color: ${props => props.active ? '#fff' : C.textSecondary}; &:hover { border-color: ${props => props.color}; } `;
const DateRow = styled.div` display: flex; gap: 8px; `;
const DateCol = styled.div` flex: 1; `;
const NumberInput = styled.input` width: 100%; padding: 8px 10px; background: ${C.inputBg}; border: 1px solid ${C.border}; border-radius: 4px; color: ${C.textPrimary}; font-size: 13px; font-family: inherit; &:focus { outline: none; border-color: ${C.accent}; background: #fff; } `;
const DeleteBtn = styled.button` width: 100%; padding: 8px; border: 1px solid ${C.danger}; border-radius: 4px; background: #fff; color: ${C.danger}; cursor: pointer; font-size: 13px; font-weight: 500; &:hover { background: ${C.danger}; color: #fff; } `;
const SaveIndicator = styled.span<{ visible: boolean }>` font-size: 11px; color: ${C.success}; opacity: ${props => props.visible ? 1 : 0}; transition: opacity 0.3s; `;

interface Props {
    milestone: Milestone | null;
    visible: boolean;
    /**
     * [T54191] The milestone's project's customer: the budget picker only
     * offers this customer's budgets. Null while unknown (project not
     * loaded yet) — the picker then simply has no options.
     */
    companyId: number | null;
    onClose: () => void;
    onChanged: () => void;
}

@observer
export default class MilestoneEditPanel extends React.Component<Props> {
    @observable justSaved = false;
    saveTimer: any = null;

    // [T54191] The sub project's commercial envelope: budgets to link to.
    // Scoped to the milestone's project's CUSTOMER — linking a budget of
    // a different customer (e.g. picked from the old unscoped list) breaks
    // the customer viewport, which walks contracts → budgets → sub
    // projects.
    budgetStore = new BudgetStore({ limit: 9999, params: { include_annotations: 'full_name' } });
    budgetsCompanyId: number | null | undefined = undefined;
    budgetsLoaded = false;

    ensureBudgets = () => {
        const companyId = this.props.companyId;
        // Unknown customer (project not loaded yet): fetch nothing rather
        // than falling back to the unscoped global list.
        if (companyId === null || companyId === undefined) {
            return;
        }
        if (this.budgetsLoaded && this.budgetsCompanyId === companyId) {
            return;
        }
        action(() => {
            this.budgetsLoaded = true;
            this.budgetsCompanyId = companyId;
            this.budgetStore.clear();
            this.budgetStore.params = { include_annotations: 'full_name', '.contract.company': companyId };
        })();
        this.budgetStore.fetch().catch((e: any) => console.error('Budget fetch failed', e));
    };

    @action handleBudgetChange = (e: React.SyntheticEvent, { value }: any) => {
        const m = this.props.milestone as any;
        m.budget = value || null;
        this.patch({ budget: value || null });
    };

    /**
     * [T54191] Options: this customer's budgets, plus the currently
     * linked budget if it is NOT one of them (e.g. a wrong link to
     * another customer made before the picker was scoped) — so it stays
     * visible and can be cleared or relinked. While the scoped list is
     * still loading, the linked budget shows under its plain name; the
     * "other customer" verdict only appears once the list proves it.
     */
    budgetOptions(m: any) {
        const options: { text: string, value: number }[] = this.budgetStore.models.map((b: any) => ({
            text: b.fullName ? `${b.fullName} (${budgetTypeLabel(b.type)})` : b.name,
            value: b.id,
        }));
        const currentId = m?.budget?.id ?? m?.budget ?? null;
        if (
            currentId !== null
            && currentId !== ''
            && !this.budgetStore.models.some((b: any) => b.id === currentId)
        ) {
            const name = m?.budget?.name || m?.budget?.fullName;
            if (name) {
                const scopedListLoaded = this.budgetsLoaded && !this.budgetStore.isLoading;
                options.unshift({
                    text: scopedListLoaded ? `${name} — other customer` : name,
                    value: currentId,
                });
            }
        }
        return options;
    }

    @action handleBudgetHours = (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value === '' ? null : Number(e.target.value);
        (this.props.milestone as any).budgetHours = v;
        this.patch({ budget_hours: v });
    };

    componentDidMount() { document.addEventListener('keydown', this.handleKeyDown); }
    componentDidUpdate(prevProps: Props) {
        // Prefetch the scoped budget list when the panel opens, so the
        // closed dropdown shows the proper budget name right away
        // (instead of waiting for the first open).
        if (this.props.visible && !prevProps.visible) {
            this.ensureBudgets();
        }
    }
    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyDown);
        if (this.saveTimer) clearTimeout(this.saveTimer);
    }
    @action handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && this.props.visible) {
            e.preventDefault();
            this.props.onClose();
        }
    };

    @action patch = async (fields: Record<string, any>) => {
        if (!this.props.milestone?.id) return;
        try {
            await api.patch('subproject/' + this.props.milestone.id + '/', fields);
            this.justSaved = true;
            if (this.saveTimer) clearTimeout(this.saveTimer);
            this.saveTimer = setTimeout(() => { this.justSaved = false; }, 1500);
            this.props.onChanged();
        } catch (e: any) { console.error('MilestoneEditPanel patch error:', e); }
    };

    @action handleName = (e: React.ChangeEvent<HTMLInputElement>) => { const v = e.target.value; (this.props.milestone as any).name = v; if (v.trim()) this.patch({ name: v }); };
    @action handleType = (v: string) => { (this.props.milestone as any).type = v; this.patch({ type: v }); };
    @action handleStatus = (v: string) => { (this.props.milestone as any).status = v; this.patch({ status: v }); };
    @action handleStart = (e: React.ChangeEvent<HTMLInputElement>) => { const v = e.target.value || null; (this.props.milestone as any).startDate = v; this.patch({ start_date: v }); };
    @action handleDeadline = (e: React.ChangeEvent<HTMLInputElement>) => { const v = e.target.value || null; (this.props.milestone as any).deadline = v; this.patch({ deadline: v }); };
    @action handleDesc = (e: React.ChangeEvent<HTMLTextAreaElement>) => { const v = e.target.value; (this.props.milestone as any).description = v; this.patch({ description: v }); };
    @action handleDelete = async () => {
        if (!this.props.milestone?.id) return;
        if (!window.confirm('Delete this milestone?')) return;
        try { await api.delete('subproject/' + this.props.milestone.id + '/'); this.props.onClose(); this.props.onChanged(); }
        catch (e: any) { console.error('Delete milestone error:', e); }
    };

    render() {
        const { milestone, visible, onClose } = this.props;
        if (!milestone) return null;
        const m = milestone as any;
        const fmt = (d: any) => { if (!d) return ''; const mm = moment(d); return mm.isValid() ? mm.format('YYYY-MM-DD') : ''; };

        return (
            <Panel visible={visible}>
                <PanelHeader>
                    <PanelTitle>{m.name || ('#' + milestone.id)}</PanelTitle>
                    <SaveIndicator visible={this.justSaved}>✓ Saved</SaveIndicator>
                    <CloseBtn onClick={onClose} data-test-milestone-panel-close>×</CloseBtn>
                </PanelHeader>
                <PanelContent>
                    <Section>
                        <SectionLabel>Name</SectionLabel>
                        <TextInput data-test-milestone-panel-name value={m.name || ''} onChange={this.handleName} />
                    </Section>
                    <Section>
                        <SectionLabel>Type</SectionLabel>
                        <BtnGroup>
                            {TYPE_OPTIONS.map(o => (
                                <BtnChoice key={o.value} active={m.type === o.value} color={o.color}
                                    onClick={() => this.handleType(o.value)} data-test-milestone-panel-type={o.value}>{o.text}</BtnChoice>
                            ))}
                        </BtnGroup>
                    </Section>
                    <Section>
                        <SectionLabel>Status</SectionLabel>
                        <BtnGroup>
                            {STATUS_OPTIONS.map(o => (
                                <BtnChoice key={o.value} active={m.status === o.value} color={o.color}
                                    onClick={() => this.handleStatus(o.value)} data-test-milestone-panel-status={o.value}>{o.text}</BtnChoice>
                            ))}
                        </BtnGroup>
                    </Section>
                    <Section>
                        <SectionLabel>Schedule</SectionLabel>
                        <DateRow>
                            <DateCol><DateInput type="date" data-test-milestone-panel-start value={fmt(m.startDate)} onChange={this.handleStart} /></DateCol>
                            <DateCol><DateInput type="date" data-test-milestone-panel-deadline value={fmt(m.deadline)} onChange={this.handleDeadline} /></DateCol>
                        </DateRow>
                    </Section>
                    <Section>
                        <SectionLabel>Phabricator Project</SectionLabel>
                        <PhabricatorProjectPicker
                            model={this.props.milestone}
                            onPhidChange={(phid) => this.patch({ phabricator_project_phid: phid })}
                        />
                    </Section>
                    <Section>
                        <SectionLabel>Budget</SectionLabel>
                        <Dropdown
                            fluid
                            selection
                            clearable
                            search
                            placeholder="Link a budget..."
                            loading={this.budgetStore.isLoading}
                            options={this.budgetOptions(m)}
                            value={m.budget?.id ?? m.budget ?? ''}
                            onOpen={this.ensureBudgets}
                            onChange={this.handleBudgetChange}
                            noResultsMessage={this.budgetStore.isLoading ? 'Loading...' : 'No budgets for this customer'}
                            data-test-milestone-panel-budget
                        />
                    </Section>
                    <Section>
                        <SectionLabel>Hour budget (own cap, optional)</SectionLabel>
                        <NumberInput
                            type="number"
                            min={0}
                            data-test-milestone-panel-budget-hours
                            value={m.budgetHours ?? ''}
                            onChange={this.handleBudgetHours}
                        />
                    </Section>
                    <Section>
                        <SectionLabel>Description</SectionLabel>
                        <TextArea data-test-milestone-panel-description value={m.description || ''} onChange={this.handleDesc} />
                    </Section>
                    <Section>
                        <DeleteBtn onClick={this.handleDelete} data-test-milestone-panel-delete>Delete Milestone</DeleteBtn>
                    </Section>
                </PanelContent>
            </Panel>
        );
    }
}
